import { Configuration } from '@/api';
import {
  billingDetailsToApi,
  toBillingDetails,
  toBillingInfo,
  toBillingPricing,
  updateSubscriptionToApi,
} from '@/api/mappers/mapBilling';
import {
  invoiceUpdateToApi,
  toGeneratedInvoices,
  toInvoice,
  toInvoices,
  toInvoiceTransaction,
  toInvoiceTransactions,
  toProcessedInvoiceTransactions,
} from '@/api/mappers/mapInvoice';
import {
  BillingApi as ApiBillingApi,
  GetBillingDetailsRequest,
  GetBillingInfoRequest,
  GetBillingPricingRequest,
  GetInvoiceRequest,
  GetInvoiceTransactionRequest,
  ListInvoicesRequest,
  ListInvoiceTransactionsRequest,
  ProcessInvoiceTransactionOperationRequest,
  ResponseError,
  UpdateBillingDetailsRequest,
  UpdateInvoiceOperationRequest,
  UpdateSubscriptionOperationRequest,
} from '@/gen/api';
import {
  BillingCustomerTarget,
  BillingDetails,
  BillingInfo,
  BillingPricing,
  SubscriptionUpdate,
} from '@/models/Billing';
import {
  GeneratedInvoices,
  Invoice,
  Invoices,
  InvoiceStatus,
  InvoiceTransaction,
  InvoiceTransactions,
  InvoiceTransactionStatus,
  InvoiceUpdate,
  ProcessedInvoiceTransactions,
} from '@/models/Invoice';

export interface InvoiceFilters {
  subscriptionId?: string;
  status?: InvoiceStatus;
  limit?: number;
  offset?: number;
}

export interface InvoiceTransactionFilters {
  invoiceId?: string;
  status?: InvoiceTransactionStatus;
  limit?: number;
  offset?: number;
}

interface BillingApi {
  getBillingInfo(customer?: BillingCustomerTarget): Promise<BillingInfo | undefined>;
  getBillingDetails(customer?: BillingCustomerTarget): Promise<BillingDetails | undefined>;
  getBillingPricing(customer?: BillingCustomerTarget): Promise<BillingPricing[]>;
  updateBillingDetails(
    details: BillingDetails,
    customer?: BillingCustomerTarget,
  ): Promise<BillingDetails>;
  updateSubscription(subscription: SubscriptionUpdate): Promise<BillingInfo>;
  generateInvoices(): Promise<GeneratedInvoices>;
  listInvoices(filters?: InvoiceFilters): Promise<Invoices>;
  getInvoice(id: string): Promise<Invoice | undefined>;
  updateInvoice(id: string, invoice: InvoiceUpdate): Promise<void>;
  generateInvoiceTransactions(): Promise<GeneratedInvoices>;
  listInvoiceTransactions(filters?: InvoiceTransactionFilters): Promise<InvoiceTransactions>;
  getInvoiceTransaction(id: string): Promise<InvoiceTransaction | undefined>;
  processInvoiceTransactions(): Promise<ProcessedInvoiceTransactions>;
  processInvoiceTransaction(id: string): Promise<InvoiceTransaction>;
}

const getApiErrorMessage = async (error: unknown, fallbackMessage: string) => {
  if (error instanceof ResponseError) {
    try {
      const body = await error.response.clone().json();

      if (body && typeof body === 'object' && 'error' in body && typeof body.error === 'string') {
        return body.error;
      }
    } catch (parseError) {
      console.error('Failed to parse billing API error response:', parseError);
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallbackMessage;
};

export class BillingApiImpl implements BillingApi {
  private billingApi: ApiBillingApi;

  constructor(configuration: Configuration) {
    this.billingApi = new ApiBillingApi(configuration);
  }

  async getBillingInfo(customer?: BillingCustomerTarget): Promise<BillingInfo | undefined> {
    try {
      const req: GetBillingInfoRequest = {
        storeId: customer?.storeId,
        supplierId: customer?.supplierId,
        retailerId: customer?.retailerId,
      };

      const response = await this.billingApi.getBillingInfo(req);
      return toBillingInfo(response);
    } catch (error) {
      console.error('Failed to get billing info:', error);
      return;
    }
  }

  async getBillingDetails(customer?: BillingCustomerTarget): Promise<BillingDetails | undefined> {
    try {
      const req: GetBillingDetailsRequest = {
        storeId: customer?.storeId,
        supplierId: customer?.supplierId,
        retailerId: customer?.retailerId,
      };

      const response = await this.billingApi.getBillingDetails(req);
      return toBillingDetails(response);
    } catch (error) {
      console.error('Failed to get billing details:', error);
      return;
    }
  }

  async getBillingPricing(customer?: BillingCustomerTarget): Promise<BillingPricing[]> {
    try {
      const req: GetBillingPricingRequest = {
        storeId: customer?.storeId,
        supplierId: customer?.supplierId,
        retailerId: customer?.retailerId,
      };

      const response = await this.billingApi.getBillingPricing(req);
      return response.items.map(toBillingPricing);
    } catch (error) {
      console.error('Failed to get billing pricing:', error);
      throw error;
    }
  }

  async updateBillingDetails(
    details: BillingDetails,
    customer?: BillingCustomerTarget,
  ): Promise<BillingDetails> {
    try {
      const req: UpdateBillingDetailsRequest = {
        billingDetailsUpdateRequest: {
          customer,
          ...billingDetailsToApi(details),
        },
      };

      const response = await this.billingApi.updateBillingDetails(req);
      return toBillingDetails(response);
    } catch (error) {
      console.error('Failed to update billing details:', error);
      throw error;
    }
  }

  async updateSubscription(subscription: SubscriptionUpdate): Promise<BillingInfo> {
    try {
      const req: UpdateSubscriptionOperationRequest = {
        updateSubscriptionRequest: updateSubscriptionToApi(subscription),
      };
      const response = await this.billingApi.updateSubscription(req);
      return toBillingInfo(response);
    } catch (error) {
      console.error('Failed to change plan:', error);
      throw error;
    }
  }

  async generateInvoices(): Promise<GeneratedInvoices> {
    try {
      const response = await this.billingApi.generateInvoices();
      return toGeneratedInvoices(response);
    } catch (error) {
      console.error('Failed to generate invoices:', error);
      throw error;
    }
  }

  async listInvoices(filters: InvoiceFilters = {}): Promise<Invoices> {
    try {
      const req: ListInvoicesRequest = {
        subscriptionId: filters.subscriptionId,
        status: filters.status,
        limit: filters.limit,
        offset: filters.offset,
      };

      const response = await this.billingApi.listInvoices(req);
      return toInvoices(response);
    } catch (error) {
      console.error('Failed to list invoices:', error);
      throw error;
    }
  }

  async getInvoice(id: string): Promise<Invoice | undefined> {
    try {
      const req: GetInvoiceRequest = { id };
      const response = await this.billingApi.getInvoice(req);
      return toInvoice(response);
    } catch (error) {
      console.error(`Failed to get invoice with id ${id}:`, error);
      return;
    }
  }

  async updateInvoice(id: string, invoice: InvoiceUpdate): Promise<void> {
    try {
      const req: UpdateInvoiceOperationRequest = {
        id,
        updateInvoiceRequest: invoiceUpdateToApi(invoice),
      };

      await this.billingApi.updateInvoice(req);
    } catch (error) {
      console.error(`Failed to update invoice with id ${id}:`, error);
      throw error;
    }
  }

  async generateInvoiceTransactions(): Promise<GeneratedInvoices> {
    try {
      const response = await this.billingApi.generateInvoiceTransactions();
      return toGeneratedInvoices(response);
    } catch (error) {
      console.error('Failed to generate invoice transactions:', error);
      throw error;
    }
  }

  async listInvoiceTransactions(
    filters: InvoiceTransactionFilters = {},
  ): Promise<InvoiceTransactions> {
    try {
      const req: ListInvoiceTransactionsRequest = {
        invoiceId: filters.invoiceId,
        status: filters.status,
        limit: filters.limit,
        offset: filters.offset,
      };

      const response = await this.billingApi.listInvoiceTransactions(req);
      return toInvoiceTransactions(response);
    } catch (error) {
      console.error('Failed to list invoice transactions:', error);
      throw error;
    }
  }

  async getInvoiceTransaction(id: string): Promise<InvoiceTransaction | undefined> {
    try {
      const req: GetInvoiceTransactionRequest = { id };
      const response = await this.billingApi.getInvoiceTransaction(req);
      return toInvoiceTransaction(response);
    } catch (error) {
      console.error(`Failed to get invoice transaction with id ${id}:`, error);
      return;
    }
  }

  async processInvoiceTransactions(): Promise<ProcessedInvoiceTransactions> {
    try {
      const response = await this.billingApi.processInvoiceTransactions();
      return toProcessedInvoiceTransactions(response);
    } catch (error) {
      console.error('Failed to process invoice transactions:', error);
      throw error;
    }
  }

  async processInvoiceTransaction(id: string): Promise<InvoiceTransaction> {
    try {
      const req: ProcessInvoiceTransactionOperationRequest = { id };
      const response = await this.billingApi.processInvoiceTransaction(req);
      return toInvoiceTransaction(response);
    } catch (error) {
      const message = await getApiErrorMessage(
        error,
        `Failed to process invoice transaction with id ${id}.`,
      );

      console.error(`Failed to process invoice transaction with id ${id}:`, error);
      throw new Error(message);
    }
  }
}

export default BillingApi;
